<template>
  <div class="correction-tab">
    <div class="correction-tab__status">
      <div
        style="font-size: 10px; color: #A5A5A5"
        class="text-right"
      >
        ※この機能はClaude 4.5 Sonnetを使用しています。
      </div>

      <CorrectionStatusSection
        :status-entries="statusEntries"
        :show-corrections-error="showCorrectionsError"
        :is-all-corrections-resolved="isAllCorrectionsResolved"
        :has-in-progress-corrections="hasInProgressCorrections"
        :has-not-requested-corrections="hasNotRequestedCorrections"
        @trigger-corrections="triggerCorrections"
      />
    </div>

    <section class="correction-tab__list">
      <div
        class="pr-4 pl-7"
        data-test="root-container"
      >
        <TransitionGroup
          name="correction-suggestions-list"
          tag="div"
        >
          <div
            v-if="groupedStatusCounts.new > 0 || staticCheckFindings.length > 0"
            key="new_header"
            class="c-correctionHeader mt-4 mb-2"
            data-test="new-header"
          >
            未修正({{ groupedStatusCounts.new + staticCheckFindings.length }})
          </div>
          <SuggestionCard
            v-for="finding in staticCheckFindings"
            :key="`static_${finding.id}`"
            :suggestion="finding"
            :selected-suggestion-id="selectedSuggestionId"
            data-test="card-static-check"
            @select="handleSelect"
            @accept="handleAcceptStaticCheck"
            @ignore="handleIgnoreStaticCheck"
          />
          <SuggestionCard
            v-for="suggestion in newSuggestions"
            :key="`new_${suggestion.id}`"
            :ref="el => registerCardRef(suggestion.id, el)"
            :suggestion="suggestion"
            :selected-suggestion-id="selectedSuggestionId"
            data-test="card-new"
            @select="handleSelect"
            @accept="handleAccept"
            @ignore="handleIgnore"
          />

          <div
            v-if="groupedStatusCounts.changed > 0"
            key="changed_header"
            class="c-correctionHeader mt-4 mb-2"
            data-test="changed-header"
          >
            修正済({{ groupedStatusCounts.changed }})
          </div>
          <SuggestionCard
            v-for="suggestion in changedSuggestions"
            :key="`changed_${suggestion.id}`"
            :ref="el => registerCardRef(suggestion.id, el)"
            :suggestion="suggestion"
            :selected-suggestion-id="selectedSuggestionId"
            data-test="card-changed"
            @select="handleSelect"
            @accept="handleAccept"
            @ignore="handleIgnore"
          />
        </TransitionGroup>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import SuggestionCard from "./SuggestionCard.vue";
import CorrectionStatusSection from "./CorrectionStatusSection.vue";
import { useCorrectionCardScroll } from "@/vue/composables/useCorrectionCardScroll";
import { createConsultationCorrectionsPolling } from "@/vue/composables/useConsultationCorrectionsPolling";

export default {
  name: "AnytimeConsultationCorrectionTab",
  components: {
    CorrectionStatusSection,
    SuggestionCard,
  },
  data() {
    return {
      correctionsPollingHandlers: null,
      suggestionCardsScrollHandler: null,
      updateEverTriggeredDebounceTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      suggestionsWithResolution: "suggestionsWithResolution",
      selectedSuggestionId: "selectedSuggestionId",
      groupedStatusCounts: "groupedStatusCounts",
      isAllCorrectionsResolved: "isAllCorrectionsResolved",      
      hasNotRequestedCorrections: "hasNotRequestedCorrections",
      hasInProgressCorrections: "hasInProgressCorrections",
      hasFailedCorrections: "hasFailedCorrections",
      statusEntries: "statusEntries",
      staticCheckFindings: "staticCheckFindings",
      anytimeConsultationAnswer: "anytimeConsultationAnswer",
    }),
    answerContent() {
      return this.anytimeConsultationAnswer?.content ?? "";
    },
    suggestions() {
      return this.suggestionsWithResolution ?? [];
    },
    newSuggestions() {
      return this.suggestions.filter((suggestion) => suggestion.resolution === "new");
    },
    changedSuggestions() {
      return this.suggestions.filter((suggestion) => suggestion.resolution === "changed");
    },
    showCorrectionsError() {
      return this.hasFailedCorrections && !this.hasInProgressCorrections;
    },
  },
  watch: {
    selectedSuggestionId() {
      this.scrollSelectedCardIntoView();
    },
    answerContent: {
      immediate: true,
      handler() {
        // Debounce: キー入力連続中の複重実行を避ける（500ms）
        clearTimeout(this.updateEverTriggeredDebounceTimer);
        this.updateEverTriggeredDebounceTimer = setTimeout(() => {
          this.updateEverTriggeredStaticCheckRulesAction();
        }, 500);
      },
    },
  },
  mounted() {
    this.scrollSelectedCardIntoView();
  },
  created() {
    this.suggestionCardsScrollHandler = useCorrectionCardScroll();
    this.correctionsPollingHandlers = createConsultationCorrectionsPolling({
      hasInProgressCorrections: () => this.hasInProgressCorrections,
      fetchCorrectionsData: (consultationId) =>
        this.$store.dispatch("fetchCorrectionsData", { consultationId }),
    });
    this.initializeCorrections(this.$route.params.id);
  },
  beforeUnmount() {
    clearTimeout(this.updateEverTriggeredDebounceTimer);
    this.correctionsPollingHandlers?.stopPolling();
  },
  methods: {
    ...mapActions({
      requestCorrections: "requestCorrections",
      initializeCorrections: "initializeCorrections",
      acceptSuggestionAction: "acceptSuggestion",
      ignoreSuggestionAction: "ignoreSuggestion",
      ignoreStaticCheckFindingAction: "ignoreStaticCheckFinding",
      acceptStaticCheckFindingAction: "acceptStaticCheckFinding",
      selectSuggestionAction: "selectSuggestionId",
      updateEverTriggeredStaticCheckRulesAction: "updateEverTriggeredStaticCheckRules",
    }),
    triggerCorrections() {
      this.requestCorrections({
        consultationId: this.$route.params.id,
        cable: this.$cable,
        connectionHandlers: this.correctionsPollingHandlers,
      });
    },
    handleSelect(suggestionId) {
      if (suggestionId === undefined || suggestionId === null) return;
      this.selectSuggestionAction(suggestionId);
    },
    handleAccept(suggestionId) {
      if (suggestionId === undefined || suggestionId === null) return;
      this.acceptSuggestionAction(suggestionId);
    },
    handleIgnore(suggestionId) {
      if (suggestionId === undefined || suggestionId === null) return;
      this.ignoreSuggestionAction(suggestionId);
    },
    handleIgnoreStaticCheck(findingId) {
      if (findingId === undefined || findingId === null) return;
      this.ignoreStaticCheckFindingAction(findingId);
    },
    handleAcceptStaticCheck(findingId) {
      if (findingId === undefined || findingId === null) return;
      this.acceptStaticCheckFindingAction(findingId);
    },
    registerCardRef(suggestionId, componentRef) {
      this.suggestionCardsScrollHandler.registerCardRef(suggestionId, componentRef);
    },
    scrollSelectedCardIntoView() {
      this.suggestionCardsScrollHandler.scrollSelectedCardIntoView(this.selectedSuggestionId);
    },
  },
};
</script>

<style scoped lang="scss">
.correction-tab {
  padding: 20px;
}

.correction-tab__status {
  margin-top: -20px;
  margin-left: -20px;
  margin-right: -20px;
  padding: 12px 20px;
  background-color: #E8F2FE;
}

.correction-tab__panel-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.correction-tab__panel-eyebrow {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.1em;
  color: #94a3b8;
}

.correction-tab__panel-meta {
  margin: 8px 0 0;
  color: #475569;
}

.correction-tab__cards {
  margin-top: 20px;
  display: grid;
  gap: 16px;
}

.correction-tab__empty {
  margin: 24px 0 0;
  color: #475569;
  text-align: center;
}

.c-correctionHeader {
  font-size: 12px;
}

.correction-tab__static-check {
  border-top: 1px solid #e2e8f0;
  padding-top: 4px;
}

@media (max-width: 768px) {
  .correction-tab__hero {
    flex-direction: column;
  }

  .correction-tab__panel-header {
    flex-direction: column;
  }

  .correction-tab__cards {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.correction-suggestions-list-move {
  transition: transform 0.5s;
}

.correction-suggestions-list-enter-active,
.correction-suggestions-list-leave-active {
  transition: all 0.3s ease;
}

.correction-suggestions-list-leave-active {
  position: absolute;
}

.correction-suggestions-list-enter-from,
.correction-suggestions-list-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
